import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { StreamChat } from 'stream-chat';
import { api, getToken } from '../api';

const STREAM_KEY = process.env.REACT_APP_STREAM_API_KEY;

export default function ChatScreen() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const matchName = location.state?.name || 'Chat';

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [myUserId, setMyUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const channelRef = useRef(null);
  const clientRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!getToken()) { navigate('/login'); return; }
    if (!STREAM_KEY) { setError('Chat is not yet configured.'); setLoading(false); return; }

    let client;
    let channel;

    const init = async () => {
      try {
        const { token, user_id, name } = await api.post('/chat/token', {});
        setMyUserId(user_id);

        client = StreamChat.getInstance(STREAM_KEY);
        clientRef.current = client;

        if (client.userID && client.userID !== user_id) await client.disconnectUser();
        if (!client.userID) await client.connectUser({ id: user_id, name }, token);

        channel = client.channel('messaging', channelId);
        channelRef.current = channel;
        await channel.watch();
        await channel.markRead();

        setMessages([...channel.state.messages]);
        setLoading(false);

        channel.on('message.new', (e) => {
          setMessages(prev => {
            if (prev.find(m => m.id === e.message.id)) return prev;
            return [...prev, e.message];
          });
        });
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    init();

    return () => {
      channel?.stopWatching?.();
    };
  }, [channelId, navigate]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || !channelRef.current) return;
    setInput('');
    try {
      await channelRef.current.sendMessage({ text });
    } catch {}
  };

  if (loading) {
    return (
      <div style={{ height: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: '100vh', background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, gap: 20, textAlign: 'center' }}>
        <div style={{ color: 'var(--very-muted)', fontSize: 13, lineHeight: 1.7 }}>{error}</div>
        <button className="btn-ghost" onClick={() => navigate(-1)}>← Go back</button>
      </div>
    );
  }

  return (
    <div className="chat-screen">
      <div className="chat-header">
        <button className="chat-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div className="chat-header-name">{matchName}</div>
        <div style={{ width: 60 }} />
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-empty">
            Say something real.
          </div>
        )}
        {messages.map(msg => (
          <div
            key={msg.id}
            className={`chat-bubble ${msg.user?.id === myUserId ? 'mine' : 'theirs'}`}
          >
            <div className="chat-bubble-text">{msg.text}</div>
            <div className="chat-bubble-time">
              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <form className="chat-input-bar" onSubmit={send}>
        <input
          className="chat-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Write something..."
          autoComplete="off"
          autoCorrect="off"
        />
        <button type="submit" className="chat-send-btn" disabled={!input.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}
